import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Briefcase, Clock, DollarSign, Users, TrendingUp, Calendar,
  Plus, FileText, Play, Pause, CheckCircle, Loader2
} from "lucide-react";
import axios from "axios";

const Projects = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const [projectForm, setProjectForm] = useState({
    name: "", code: "", customer_id: "", description: "",
    start_date: "", end_date: "", budget_hours: "", budget_amount: "",
    hourly_rate: "", status: "planning"
  });

  const [timeForm, setTimeForm] = useState({
    project_id: "", date: new Date().toISOString().split('T')[0],
    hours: "", description: "", billable: true
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, customersRes] = await Promise.all([
        axios.get(`${API}/projects${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, 
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectDetails(res.data);
      setSelectedProject(projectId);
    } catch (error) {
      toast.error("Kunde inte hämta projektdetaljer");
    }
  };

  const fetchTimeEntries = async (projectId) => {
    try {
      const res = await axios.get(`${API}/time-entries?project_id=${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeEntries(res.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const createProject = async () => {
    try {
      await axios.post(`${API}/projects`, {
        ...projectForm,
        budget_hours: projectForm.budget_hours ? parseFloat(projectForm.budget_hours) : null,
        budget_amount: projectForm.budget_amount ? parseFloat(projectForm.budget_amount) : null,
        hourly_rate: projectForm.hourly_rate ? parseFloat(projectForm.hourly_rate) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Projekt skapat");
      setShowProjectDialog(false);
      resetProjectForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa projekt");
    }
  };

  const createTimeEntry = async () => {
    if (!timeForm.project_id || !timeForm.hours) {
      toast.error("Fyll i projekt och antal timmar");
      return;
    }
    
    try {
      await axios.post(`${API}/time-entries`, {
        ...timeForm,
        hours: parseFloat(timeForm.hours)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tid registrerad");
      setShowTimeDialog(false);
      setTimeForm(prev => ({ ...prev, hours: "", description: "" }));
      
      if (selectedProject) {
        fetchProjectDetails(selectedProject);
        fetchTimeEntries(selectedProject);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte registrera tid");
    }
  };

  const generateInvoice = async (projectId) => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/generate-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Faktura ${res.data.invoice_number} skapad på ${res.data.total.toLocaleString('sv-SE')} kr`);
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa faktura");
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      name: "", code: "", customer_id: "", description: "",
      start_date: "", end_date: "", budget_hours: "", budget_amount: "",
      hourly_rate: "", status: "planning"
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: "bg-gray-100 text-gray-700",
      active: "bg-green-100 text-green-700",
      on_hold: "bg-amber-100 text-amber-700",
      completed: "bg-blue-100 text-blue-700",
      cancelled: "bg-red-100 text-red-700"
    };
    const labels = {
      planning: "Planering", active: "Aktivt", on_hold: "Pausat",
      completed: "Avslutat", cancelled: "Avbrutet"
    };
    return <Badge className={styles[status] || ""}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="projects-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Projektredovisning</h1>
          <p className="text-muted-foreground">Hantera projekt, tid och fakturering</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Clock className="h-4 w-4 mr-2" />
                Registrera tid
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registrera tid</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Projekt</Label>
                  <Select value={timeForm.project_id} onValueChange={(v) => setTimeForm({ ...timeForm, project_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj projekt" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.filter(p => p.status === 'active').map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input 
                      type="date"
                      value={timeForm.date}
                      onChange={(e) => setTimeForm({ ...timeForm, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Timmar</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={timeForm.hours}
                      onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })}
                      placeholder="8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Beskrivning</Label>
                  <Textarea 
                    value={timeForm.description}
                    onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })}
                    placeholder="Vad har du arbetat med?"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={timeForm.billable}
                    onCheckedChange={(v) => setTimeForm({ ...timeForm, billable: v })}
                  />
                  <Label>Debiterbar tid</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTimeDialog(false)}>Avbryt</Button>
                <Button onClick={createTimeEntry}>Registrera</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nytt projekt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Skapa projekt</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Projektnamn</Label>
                  <Input 
                    value={projectForm.name}
                    onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                    placeholder="Webbplats redesign"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Projektkod</Label>
                  <Input 
                    value={projectForm.code}
                    onChange={(e) => setProjectForm({ ...projectForm, code: e.target.value })}
                    placeholder="WEB-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kund</Label>
                  <Select value={projectForm.customer_id || "none"} onValueChange={(v) => setProjectForm({ ...projectForm, customer_id: v === "none" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kund" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ingen kund</SelectItem>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timpris</Label>
                  <Input 
                    type="number"
                    value={projectForm.hourly_rate}
                    onChange={(e) => setProjectForm({ ...projectForm, hourly_rate: e.target.value })}
                    placeholder="950"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Startdatum</Label>
                  <Input 
                    type="date"
                    value={projectForm.start_date}
                    onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slutdatum</Label>
                  <Input 
                    type="date"
                    value={projectForm.end_date}
                    onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Budgeterade timmar</Label>
                  <Input 
                    type="number"
                    value={projectForm.budget_hours}
                    onChange={(e) => setProjectForm({ ...projectForm, budget_hours: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Budgeterat belopp (kr)</Label>
                  <Input 
                    type="number"
                    value={projectForm.budget_amount}
                    onChange={(e) => setProjectForm({ ...projectForm, budget_amount: e.target.value })}
                    placeholder="95000"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Beskrivning</Label>
                  <Textarea 
                    value={projectForm.description}
                    onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                    placeholder="Projektbeskrivning..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Avbryt</Button>
                <Button onClick={createProject}>Skapa</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aktiva projekt</p>
                <p className="text-2xl font-bold">{projects.filter(p => p.status === 'active').length}</p>
              </div>
              <Briefcase className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totala timmar</p>
                <p className="text-2xl font-bold">{projects.reduce((sum, p) => sum + (p.total_hours || 0), 0)}</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total intäkt</p>
                <p className="text-2xl font-bold">{projects.reduce((sum, p) => sum + (p.total_revenue || 0), 0).toLocaleString('sv-SE')} kr</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Att fakturera</p>
                <p className="text-2xl font-bold text-green-600">
                  {projects.reduce((sum, p) => sum + (p.total_revenue || 0) - (p.invoiced_amount || 0), 0).toLocaleString('sv-SE')} kr
                </p>
              </div>
              <FileText className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Project List */}
        <div className="col-span-1 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Projekt</h3>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="active">Aktiva</SelectItem>
                <SelectItem value="planning">Planering</SelectItem>
                <SelectItem value="completed">Avslutade</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            {projects.map(project => (
              <Card 
                key={project.id} 
                className={`cursor-pointer transition-colors ${selectedProject === project.id ? 'border-primary' : 'hover:bg-muted/50'}`}
                onClick={() => { fetchProjectDetails(project.id); fetchTimeEntries(project.id); }}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{project.name}</p>
                      <p className="text-sm text-muted-foreground">{project.code}</p>
                      {project.customer_name && (
                        <p className="text-sm text-muted-foreground">{project.customer_name}</p>
                      )}
                    </div>
                    {getStatusBadge(project.status)}
                  </div>
                  {project.budget_hours && (
                    <div className="mt-2">
                      <Progress value={(project.total_hours / project.budget_hours) * 100} className="h-1" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {project.total_hours || 0} / {project.budget_hours} timmar
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {projects.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Inga projekt. Skapa ditt första!
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Project Details */}
        <div className="col-span-2">
          {projectDetails ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{projectDetails.name}</CardTitle>
                    <CardDescription>{projectDetails.code} {projectDetails.customer_name && `• ${projectDetails.customer_name}`}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {projectDetails.uninvoiced?.grand_total > 0 && (
                      <Button onClick={() => generateInvoice(projectDetails.id)}>
                        <FileText className="h-4 w-4 mr-2" />
                        Fakturera ({projectDetails.uninvoiced.grand_total.toLocaleString('sv-SE')} kr)
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profitability */}
                {projectDetails.profitability && (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Timmar</p>
                      <p className="text-xl font-semibold">{projectDetails.profitability.total_hours}</p>
                      {projectDetails.budget_hours && (
                        <p className="text-xs text-muted-foreground">
                          {projectDetails.profitability.budget_hours_used?.toFixed(0)}% av budget
                        </p>
                      )}
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Intäkter</p>
                      <p className="text-xl font-semibold">{projectDetails.profitability.total_revenue?.toLocaleString('sv-SE')} kr</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Kostnader</p>
                      <p className="text-xl font-semibold">{projectDetails.profitability.total_cost?.toLocaleString('sv-SE')} kr</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Marginal</p>
                      <p className={`text-xl font-semibold ${projectDetails.profitability.margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {projectDetails.profitability.margin_percent?.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                )}

                {/* Budget Progress */}
                {projectDetails.budget_hours && (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Timbudget</span>
                      <span>{projectDetails.profitability.total_hours} / {projectDetails.budget_hours} timmar</span>
                    </div>
                    <Progress value={(projectDetails.profitability.total_hours / projectDetails.budget_hours) * 100} />
                  </div>
                )}

                {/* Recent Time Entries */}
                <div>
                  <h4 className="font-semibold mb-2">Senaste tidrapporter</h4>
                  <div className="space-y-2">
                    {timeEntries.slice(0, 5).map(entry => (
                      <div key={entry.id} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                        <div>
                          <p className="text-sm font-medium">{entry.description || 'Ingen beskrivning'}</p>
                          <p className="text-xs text-muted-foreground">{entry.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono">{entry.hours} tim</p>
                          <p className="text-xs text-muted-foreground">{entry.amount?.toLocaleString('sv-SE')} kr</p>
                        </div>
                      </div>
                    ))}
                    {timeEntries.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Ingen tid registrerad på detta projekt
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Välj ett projekt för att se detaljer</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
